import Card from '@mui/material/Card';
import { Box, Grid2, Skeleton, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { IEventSimpleProps } from '../../../services/Events/types';
import axios from 'axios';
import { api } from '../../../services/DefaultService';
import { useGetFaceRecognitionEventByIdAsync } from '../../../services/Events/FaceRecognitionEventService/hooks/useMutation';
import { useGetIvsEventByIdAsync } from '../../../services/Events/IvsEventsService/hooks/useMutation';
import { useGetStudentRecognitionEventByIdAsync } from '../../../services/Events/StudentRecognitionEventsService/hooks/useMutation';
import { useGetResponsibleRecognitionEventByIdAsync } from '../../../services/Events/ResponsibleRecognitionEventsService/hooks/useMutation';
import { useGetLprEventByIdAsync } from '../../../services/Events/LprEventsService/hooks/useMutation';

interface LastEventProps {
  title: string;
  event: IEventSimpleProps | undefined;
  onClickInImage?: (imageUrl: string) => void;
  width?: string;
}

interface IEventDetailsList {
  label: string;
  value: any;
}

const CardLastEvent = ({
  event,
  onClickInImage,
  title,
  width,
}: LastEventProps) => {
  const [imageToRender, setImageToRender] = useState<string>();
  const [eventDetails, setEventDetails] = useState<any>();

  const { mutateAsync: mutateGetFaceRecognitionEventAsync } =
    useGetFaceRecognitionEventByIdAsync();
  const { mutateAsync: mutateGetLprEventAsync } = useGetLprEventByIdAsync();
  const { mutateAsync: mutateGetResponsibleEventAsync } =
    useGetResponsibleRecognitionEventByIdAsync();
  const { mutateAsync: mutateGetStudentEventAsync } =
    useGetStudentRecognitionEventByIdAsync();
  const { mutateAsync: mutateGetIvsEventAsync } = useGetIvsEventByIdAsync();
  const [isVisibleEditionDialog, setIsVisibleEditionDialog] =
    useState<boolean>(false);

  const getImage = useCallback(async () => {
    if (event) {
      const image = event.image || event.carImage || event.recognitionImage;
      if (image) {
        try {
          const response = await axios.get(
            api.getUri() + '/v1/images/' + image,
            {
              responseType: 'blob',
            },
          );
          return URL.createObjectURL(response.data);
        } catch (error) {
          console.error('Erro ao carregar a imagem:', error);
        }
      }
    } else {
      return undefined;
    }
  }, [event]);

  const createLprDetails = (details: IEventDetailsList[]) => {
    for (const prop in eventDetails) {
      if (eventDetails[prop]) {
        if (prop === 'plate')
          details.push({
            label: 'Placa',
            value: eventDetails[prop],
          });
      }
    }
    details.push({
      label: 'Horário',
      value: eventDetails.createdAt,
    });
  };

  const createResponsableDetails = (details: IEventDetailsList[]) => {
    if (eventDetails.clientName) {
      details.push({
        label: 'Nome',
        value: eventDetails.clientName,
      });
    }
    details.push({
      label: 'Horário',
      value: eventDetails.createdAt,
    });
  };

  const createStudentDetails = (details: IEventDetailsList[]) => {
    if (eventDetails.clientName) {
      details.push({
        label: 'Nome',
        value: eventDetails.clientName,
      });
    }
    details.push({
      label: 'Horário',
      value: eventDetails.createdAt,
    });
  };

  const createFacialDetails = (details: IEventDetailsList[]) => {
    if (eventDetails.clientName) {
      details.push({
        label: 'Horário',
        value: eventDetails.createdAt,
      });
    }
  };

  const getValuesToRenderByEventType = (): any[] => {
    const details: IEventDetailsList[] = [];
    if (event?.id && eventDetails) {
      switch (event.discriminator) {
        case 'FaceRecognitionEventEntity':
          createFacialDetails(details);
          break;
        case 'LprEventEntity':
          createLprDetails(details);
          break;
        case 'IvsEventEntity':
          break;
        case 'StudentRecognitionEventEntity':
          createStudentDetails(details);
          break;
        case 'ResponsibleRecognitionEventEntity':
          createResponsableDetails(details);
          break;
      }
    }
    return details;
  };

  const renderEventDetails = () => {
    const valuesToRender: IEventDetailsList[] = getValuesToRenderByEventType();

    return valuesToRender.map((item, index) => (
      <Box
        key={index}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        justifyContent={'start'}
        height={'100%'}
      >
        <Box>
          <Typography sx={{ fontWeight: 'bold', color: 'var(--app-accent-muted)' }}>
            {item.label}
          </Typography>
        </Box>
        <Box>
          <Typography fontSize={'15px'}>{item.value}</Typography>
        </Box>
      </Box>
    ));
  };

  useEffect(() => {
    if (event?.id) {
      switch (event.discriminator) {
        case 'FaceRecognitionEventEntity':
          mutateGetFaceRecognitionEventAsync(event?.id).then(setEventDetails);
          break;
        case 'LprEventEntity':
          mutateGetLprEventAsync(event?.id).then(setEventDetails);
          break;
        case 'IvsEventEntity':
          mutateGetIvsEventAsync(event?.id).then(setEventDetails);
          break;
        case 'StudentRecognitionEventEntity':
          mutateGetStudentEventAsync(event?.id).then(setEventDetails);
          break;
        case 'ResponsibleRecognitionEventEntity':
          mutateGetResponsibleEventAsync(event?.id).then(setEventDetails);
          break;
      }
    }
  }, [event]);

  useEffect(() => {
    if (event) {
      let imageUrl = '';
      if (event.carImage && event.carImage.indexOf('https') > -1) {
        imageUrl = event.carImage;
      } else if (
        event.recognitionImage &&
        event.recognitionImage.indexOf('https') > -1
      ) {
        imageUrl = event.recognitionImage;
      } else if (event.image && event.image.indexOf('https') > -1) {
        imageUrl = event.image;
      }
      setImageToRender(imageUrl);
    }
  }, [event]);
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        p: 2,
        backgroundColor: 'var(--app-surface-2)',
        position: 'relative',
        boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        borderRadius: 4,
      }}
    >
      <Grid2
        container
        columnSpacing={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid2
          sx={{
            display: 'flex',
            width: width || '100%',
            height: 200,
            margin: '1px',
            justifyContent: 'center',
          }}
        >
          {!imageToRender ? (
            <Skeleton variant="rounded" width={'100%'} height={200} />
          ) : (
            <>
              <img
                width={'100%'}
                height={200}
                src={imageToRender}
                alt="event_img"
                onClick={() =>
                  onClickInImage &&
                  onClickInImage(
                    event?.recognitionImage ||
                      event?.carImage ||
                      event?.image ||
                      '',
                  )
                }
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  cursor: 'pointer',
                  borderRadius: 8,
                }}
              />
            </>
          )}
        </Grid2>

        <Grid2
          container
          display={'flex'}
          justifyContent={'left'}
          alignContent={'center'}
          height={'3vh'}
          width={'100%'}
          marginTop={4}
        >
          <Grid2 container>
            <Grid2 size={5}>
              <Typography
                variant={'h6'}
                sx={{ fontWeight: 'bold', color: 'var(--app-accent-muted)' }}
              >
                DISPOSITIVO
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {event && event.device?.name}
              </Typography>
            </Grid2>
          </Grid2>
          {renderEventDetails()}
        </Grid2>
      </Grid2>
    </Card>
  );
};

export default CardLastEvent;
